/**
 * The Activity table's Screen cell.
 *
 * Leads with the real resolution. On Windows, Linux, phones and tablets that is
 * CSS size × pixel ratio. On a Mac at @2x it is only the size macOS renders at:
 * a scaled mode (1710×1107 on a 2880×1864 panel) renders larger than the panel
 * and scales down. So a Mac leads with the native panel when the looks-like
 * size pins down the model, and otherwise shows the product labelled
 * "rendered".
 * Below it, a labelled guess at the display (`estimateDisplay`), then the exact
 * CSS screen, ratio and window as the browser reported them at landing sign-in:
 * `@1.25x` stays `@1.25x`.
 */
import { estimateDisplay, isScaledRender, nativePanel } from './displayEstimate'

export interface ScreenValue {
  screenW: number | null
  screenH: number | null
  viewportW: number | null
  viewportH: number | null
  dpr: number | null
  /** Chromium's device model, Android only. */
  deviceModel?: string | null
  /** The capture's device class and OS, from its User-Agent. */
  device?: string | null
  os?: string | null
  /** Not captured on this row: the person's latest earlier landing sign-in. */
  inherited?: boolean
  /** When it was captured. */
  fromTs?: number
}

export interface ScreenDisplay {
  /** The real resolution, or whatever there is when it cannot be computed. */
  primary: string
  /** A guess at the display, always shown as one ("≈ …"). */
  estimate: string | null
  /** The exact CSS values. */
  secondary: string | null
  title: string | null
  inherited: boolean
}

const EMPTY: ScreenDisplay = { primary: '—', estimate: null, secondary: null, title: null, inherited: false }

const size = (w: number | null, h: number | null) => `${w ?? '?'}×${h ?? '?'}`

export function formatScreen(
  s: ScreenValue | null | undefined,
  formatTime: (ts: number) => string = ts => new Date(ts).toLocaleString(),
): ScreenDisplay {
  if (!s) return EMPTY
  const hasScreen = s.screenW !== null || s.screenH !== null
  const hasWindow = s.viewportW !== null || s.viewportH !== null
  if (!hasScreen && !hasWindow && s.dpr === null) return EMPTY

  const ratio = s.dpr !== null ? `@${s.dpr}x` : null
  const windowSize = hasWindow ? `window ${size(s.viewportW, s.viewportH)}` : null
  const product = s.dpr !== null && s.screenW !== null && s.screenH !== null
    ? `${Math.round(s.screenW * s.dpr)}×${Math.round(s.screenH * s.dpr)}`
    : null
  const scaled = isScaledRender(s)
  const panel = nativePanel(s)
  const rendered = product && scaled ? `rendered ${product}` : null
  const css = hasScreen ? [`CSS ${size(s.screenW, s.screenH)}`, ratio].filter(Boolean).join(' ') : null

  // Whatever there is goes on the first line, so a partial capture never
  // renders as an empty cell with a subtitle.
  let primary: string
  let secondary: string | null
  if (panel) {
    primary = panel
    // At the default looks-like size the render is the panel: say it once.
    secondary = [css, product === panel ? null : rendered, windowSize].filter(Boolean).join(' · ')
  } else if (product) {
    primary = rendered ?? product
    secondary = [css, windowSize].filter(Boolean).join(' · ')
  } else if (hasScreen) {
    primary = [size(s.screenW, s.screenH), ratio].filter(Boolean).join(' ')
    secondary = windowSize
  } else {
    primary = [windowSize, ratio].filter(Boolean).join(' ')
    secondary = null
  }

  const guess = estimateDisplay(s)
  const estimate = guess ? `≈ ${guess}` : null

  const parts: string[] = []
  if (panel) parts.push(`Resolution ${panel} native panel px (estimated from the model)`)
  if (product) {
    parts.push(scaled
      ? `rendered ${product} px (CSS × pixel ratio; macOS may scale it to the panel)`
      : `Resolution ${product} physical px`)
  }
  if (hasScreen) parts.push(`screen ${size(s.screenW, s.screenH)} CSS px`)
  if (s.dpr !== null) parts.push(`pixel ratio ${s.dpr}`)
  if (windowSize) parts.push(windowSize)
  if (s.deviceModel) parts.push(`model ${s.deviceModel}`)
  if (guess) parts.push(`estimated display: ${guess} (a guess from the values above)`)
  if (s.inherited && s.fromTs !== undefined) parts.push(`from sign-in ${formatTime(s.fromTs)}`)

  return { primary, estimate, secondary, title: parts.join(' · '), inherited: s.inherited === true }
}
